import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Form, Button, Table, Badge } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import moment from 'moment';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import * as api from '~/Services/Service';

const ITENS_POR_PAGINA_OPTIONS = [
  { value: 10, label: '10' },
  { value: 20, label: '20' },
  { value: 50, label: '50' },
  { value: 100, label: '100' },
];

const LogAtualizacoes = () => {
  const [dataInicio, setDataInicio] = useState(moment().startOf('month').format('YYYY-MM-DD'));
  const [dataFim, setDataFim] = useState(moment().format('YYYY-MM-DD'));
  const [usuario, setUsuario] = useState(null);
  const [tipoLog, setTipoLog] = useState(null);
  const [codigo, setCodigo] = useState(null);
  const [resultados, setResultados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [consultaRealizada, setConsultaRealizada] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(20);
  const [tiposLog, setTiposLog] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [codigos, setCodigos] = useState([]);
  const [labelCodigo, setLabelCodigo] = useState('Código');
  const [periodoAlteradoManualmente, setPeriodoAlteradoManualmente] = useState(false);

  // Carga inicial: tipos de log e usuários
  useEffect(() => {
    carregarTiposLog();
    carregarUsuarios();
  }, []);

  // Carrega códigos distintos quando o tipo de log ou filtros mudam
  useEffect(() => {
    if (tipoLog) {
      setLabelCodigo(tipoLog.label_codigo || 'Código');
      carregarCodigos();
    } else {
      setCodigos([]);
      setCodigo(null);
      setLabelCodigo('Código');
    }
  }, [tipoLog, dataInicio, dataFim, usuario]); 

  // RN-07: ajuste automático do período conforme filtros opcionais preenchidos
  useEffect(() => {
    if (periodoAlteradoManualmente) return;
    const filtrosPreenchidos = (usuario ? 1 : 0) + (tipoLog ? 1 : 0);
    if (filtrosPreenchidos === 0) {
      setDataInicio(moment().startOf('month').format('YYYY-MM-DD'));
    } else if (filtrosPreenchidos === 1) {
      setDataInicio(moment().subtract(3, 'months').format('YYYY-MM-DD'));
    } else {
      setDataInicio(moment().subtract(12, 'months').format('YYYY-MM-DD'));
    }
  }, [usuario, tipoLog]); 

  const carregarTiposLog = async () => {
    try {
      const res = await api.buscarTiposLog();
      setTiposLog(
        (res || []).map((t) => ({
          value: t.codigo,
          label: t.codigo + ' - ' + t.descricao,
          label_codigo: t.label_codigo || 'Código',
        }))
      );
    } catch (e) {
      toast.error('Erro ao carregar tipos de log');
    }
  };

  const carregarUsuarios = async () => {
    try {
      const res = await api.buscarUsuarioOptions('todos');
      setUsuarios(
        (res || []).map((u) => ({
          value: u.codigo_usuario,
          label: u.codigo_usuario + ' - ' + (u.nome_completo || u.nome || ''),
        }))
      );
    } catch (e) {
      toast.error('Erro ao carregar usuários');
    }
  };

  const carregarCodigos = async () => {
    try {
      const body = {
        data_inicio: dataInicio,
        data_fim: dataFim,
        usuario: usuario ? usuario.value : null,
        tipo_log: tipoLog ? tipoLog.value : null,
      };
      const res = await api.buscarCodigosLog(body);
      setCodigos(
        (res || []).map((c) => ({ value: c, label: String(c) }))
      );
    } catch (e) {
      setCodigos([]);
    }
  };

  const executarConsulta = async (pag, itens) => {
    setLoading(true);
    try {
      const body = {
        data_inicio: dataInicio,
        data_fim: dataFim,
        usuario: usuario ? usuario.value : null,
        tipo_log: tipoLog ? tipoLog.value : null,
        codigo: codigo ? codigo.value : null,
        pagina: pag,
        itens_por_pagina: itens,
      };
      const res = await api.buscarLogAtualizacoes(body);
      if (!res || !res.itens || res.itens.length === 0) {
        toast.warning('Não existem dados para o período');
        setResultados(null);
      } else {
        setResultados(res);
      }
      setConsultaRealizada(true);
    } catch (e) {
      toast.error('Erro ao consultar logs de auditoria');
    } finally {
      setLoading(false);
    }
  };

  const handleConsultar = async () => {
    if (!dataInicio) { toast.error('Data inicial é obrigatória'); return; }
    if (!dataFim) { toast.error('Data final é obrigatória'); return; }
    if (dataInicio > dataFim) { toast.error('Período inválido'); return; }
    setPagina(1);
    await executarConsulta(1, itensPorPagina);
  };

  const handleLimpar = () => {
    setDataInicio(moment().startOf('month').format('YYYY-MM-DD'));
    setDataFim(moment().format('YYYY-MM-DD'));
    setUsuario(null);
    setTipoLog(null);
    setCodigo(null);
    setLabelCodigo('Código');
    setCodigos([]);
    setResultados(null);
    setConsultaRealizada(false);
    setPeriodoAlteradoManualmente(false);
  };

  const handleMudarPagina = async (novaPagina) => {
    setPagina(novaPagina);
    await executarConsulta(novaPagina, itensPorPagina);
  };

  const handleMudarItensPorPagina = async (novosItens) => {
    setItensPorPagina(novosItens);
    setPagina(1);
    await executarConsulta(1, novosItens);
  };

  const renderPaginacao = () => {
    if (!resultados) return null;
    const total = resultados.total;
    const totalPaginas = resultados.total_paginas;
    const inicio = total === 0 ? 0 : (pagina - 1) * itensPorPagina + 1;
    const fim = Math.min(pagina * itensPorPagina, total);
    return (
      <div className="d-flex justify-content-between align-items-center flex-wrap px-3 py-2 border-top" style={{ gap: '6px' }}>
        <small className="text-muted" style={{ whiteSpace: 'nowrap' }}>
          Mostrando {inicio}–{fim} de {total} {total === 1 ? 'registro' : 'registros'}
        </small>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center', gap: '6px' }}>
          {/* grupo Itens/pág */}
          <div className="d-flex align-items-center">
            <small className="mr-1 text-muted">Itens:</small>
            <Form.Control
              as="select"
              size="sm"
              value={itensPorPagina}
              onChange={(e) => handleMudarItensPorPagina(Number(e.target.value))}
              style={{ width: 65 }}
            >
              {ITENS_POR_PAGINA_OPTIONS.map((op) => (
                <option key={op.value} value={op.value}>{op.label}</option>
              ))}
            </Form.Control>
          </div>
          {/* grupo navegação */}
          <div className="d-flex align-items-center">
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => handleMudarPagina(1)}
              disabled={pagina <= 1}
              className="mr-1"
              title="Primeira página"
            >{'«'}</Button>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => handleMudarPagina(pagina - 1)}
              disabled={pagina <= 1}
              className="mr-1"
              title="Página anterior"
            >{'‹'}</Button>
            <span className="mx-2 text-muted" style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
              {pagina}/{totalPaginas}
            </span>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => handleMudarPagina(pagina + 1)}
              disabled={pagina >= totalPaginas}
              className="mr-1"
              title="Próxima página"
            >{'›'}</Button>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => handleMudarPagina(totalPaginas)}
              disabled={pagina >= totalPaginas}
              title="Última página"
            >{'»'}</Button>
          </div>
        </div>
      </div>
    );
  };


  return (
    <Container fluid className="px-3 py-3">
      <style>{'\
        .log-desktop { display: block; }\
        .log-mobile  { display: none; }\
        .log-filtros-row > [class*="col-"] { margin-bottom: 0.75rem; }\
        @media (max-width: 991px) {\
          .log-desktop { display: none !important; }\
          .log-mobile  { display: block; overflow: hidden; }\
        }\
        @media (max-width: 424px) {\
          .log-periodo-inputs { flex-direction: column !important; align-items: stretch !important; }\
          .log-periodo-sep { display: none !important; }\
        }\
      '}</style>

      <Header title="Log de Atualizações" />

      {/* Card de Filtros */}
      <Card className="shadow-sm mb-3">
        <Card.Header
          className="py-2"
          style={{ backgroundColor: '#0747a6', color: '#fff' }}
        >
          <strong>Filtros</strong>
        </Card.Header>
        <Card.Body className="pb-1 pt-3">
          <Row className="log-filtros-row align-items-end">
            <Col xs={12} md={6} lg={4}>
              <Form.Label className="mb-1 small font-weight-bold">Período</Form.Label>
              <div className="d-flex align-items-center log-periodo-inputs">
                <Form.Control
                  type="date"
                  value={dataInicio}
                  onChange={(e) => { setDataInicio(e.target.value); setPeriodoAlteradoManualmente(true); }}
                  style={{ height: 42, minWidth: 0, flex: 1 }}
                />
                <span className="mx-1 text-muted small flex-shrink-0 log-periodo-sep">a</span>
                <Form.Control
                  type="date"
                  value={dataFim}
                  onChange={(e) => { setDataFim(e.target.value); setPeriodoAlteradoManualmente(true); }}
                  style={{ height: 42, minWidth: 0, flex: 1 }}
                />
              </div>
            </Col>
            <Col xs={12} md={6} lg={3}>
              <Form.Label className="mb-1 small font-weight-bold">Usuário</Form.Label>
              <Select
                options={usuarios}
                value={usuario}
                onChange={(val) => { setUsuario(val); setCodigo(null); }}
                isClearable
                isSearchable
                placeholder="Todos"
                noOptionsMessage={() => 'Nenhum usuário encontrado'}
                styles={customStyles}
                components={{ DropdownIndicator }}
              />
            </Col>
            <Col xs={12} md={6} lg={3}>
              <Form.Label className="mb-1 small font-weight-bold">Tipo de Log</Form.Label>
              <Select
                options={tiposLog}
                value={tipoLog}
                onChange={(val) => { setTipoLog(val); setCodigo(null); }}
                isClearable
                isSearchable
                placeholder="Todos"
                noOptionsMessage={() => 'Nenhum tipo encontrado'}
                styles={customStyles}
                components={{ DropdownIndicator }}
              />
            </Col>
            <Col xs={12} md={6} lg={2}>
              <Form.Label className="mb-1 small font-weight-bold">{labelCodigo}</Form.Label>
              <Select
                options={codigos}
                value={codigo}
                onChange={(val) => setCodigo(val)}
                isClearable
                isSearchable
                isDisabled={!tipoLog}
                placeholder={tipoLog ? 'Todos' : '—'}
                noOptionsMessage={() => 'Nenhum código disponível'}
                styles={customStyles}
                components={{ DropdownIndicator }}
              />
            </Col>
          </Row>
          <Row className="mt-1 mb-2">
            <Col className="d-flex justify-content-end">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleLimpar}
                disabled={loading}
                className="mr-2"
              >
                Limpar
              </Button>
              <Button
                size="sm"
                onClick={handleConsultar}
                disabled={loading}
                style={{ backgroundColor: '#7bb32e', borderColor: '#7bb32e', color: '#fff' }}
              >
                {loading ? 'Consultando...' : 'Consultar'}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Card de Dados */}
      {consultaRealizada && (
        <Card className="shadow-sm">
          <Card.Header
            className="d-flex justify-content-between align-items-center py-2"
            style={{ backgroundColor: '#0747a6', color: '#fff' }}
          >
            <strong>Registros de Log</strong>
            {resultados && (
              <Badge variant="light">
                {resultados.total} {resultados.total === 1 ? 'registro' : 'registros'}
              </Badge>
            )}
          </Card.Header>
          <Card.Body className="p-0">
            {loading ? (
              <Loading />
            ) : !resultados || resultados.itens.length === 0 ? (
              <p className="text-muted text-center my-4">Nenhum registro encontrado para os filtros informados.</p>
            ) : (
              <>
                {/* Desktop */}
                <div className="log-desktop" style={{ overflowX: 'auto' }}>
                  <Table striped hover size="sm" className="mb-0" style={{ borderCollapse: 'collapse' }}>
                    <thead className="thead-light">
                      <tr>
                        <th style={{ whiteSpace: 'nowrap', width: 148 }}>Data/Hora</th>
                        <th style={{ whiteSpace: 'nowrap', width: 68 }}>Usuário</th>
                        <th style={{ whiteSpace: 'nowrap', width: 160 }}>Nome</th>
                        <th style={{ whiteSpace: 'nowrap', width: 52, textAlign: 'center' }}>Tipo</th>
                        <th style={{ whiteSpace: 'nowrap', width: 78, textAlign: 'right' }}>Código</th>
                        <th>Atualização</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultados.itens.map((item, idx) => {
                        const atur = item.atualizacao || '';
                        return (
                          <tr key={`${item.data_hora}-${item.usuario}-${idx}`}>
                            <td style={{ whiteSpace: 'nowrap', fontSize: '0.82rem' }}>
                              {moment(item.data_hora).format('DD/MM/YYYY HH:mm:ss')}
                            </td>
                            <td style={{ fontSize: '0.82rem' }}>{item.usuario != null ? item.usuario : '-'}</td>
                            <td style={{ fontSize: '0.82rem' }}>{item.nome_usuario || '-'}</td>
                            <td style={{ textAlign: 'center' }}>
                              {item.tipo_log
                                ? <Badge variant="secondary" style={{ fontSize: '0.72rem', letterSpacing: '0.03em' }}>{item.tipo_log}</Badge>
                                : '-'}
                            </td>
                            <td style={{ textAlign: 'right', fontSize: '0.82rem' }}>
                              {item.codigo != null ? item.codigo : '-'}
                            </td>
                            <td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.82rem' }}>
                              {atur || '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>

                {/* Mobile — cards */}
                <div className="log-mobile p-2">
                  {resultados.itens.map((item, idx) => (
                      <Card key={`${item.data_hora}-${item.usuario}-${idx}`} className="mb-2" style={{ border: '1px solid #dee2e6' }}>
                        <Card.Body className="py-2 px-3">
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <span style={{ fontWeight: 600, fontSize: '0.88rem', color: '#212529' }}>
                              {moment(item.data_hora).format('DD/MM/YYYY HH:mm:ss')}
                            </span>
                            {item.tipo_log && (
                              <Badge variant="secondary" style={{ fontSize: '0.72rem', letterSpacing: '0.03em' }}>
                                {item.tipo_log}
                              </Badge>
                            )}
                          </div>
                          <div style={{ fontSize: '0.82rem', color: '#495057', marginBottom: 4 }}>
                            <span style={{ fontWeight: 600 }}>{item.usuario != null ? item.usuario : '-'}</span>
                            {item.nome_usuario ? <span className="text-muted"> — {item.nome_usuario}</span> : null}
                            {item.codigo != null && (
                              <span className="text-muted"> · Cód.&nbsp;{item.codigo}</span>
                            )}
                          </div>
                          {item.atualizacao && (
                            <div style={{ fontSize: '0.8rem', color: '#555', borderTop: '1px solid #f0f0f0', paddingTop: 5, marginTop: 4, overflowWrap: 'break-word', wordBreak: 'break-word', whiteSpace: 'pre-wrap', maxWidth: '100%' }}>
                              {item.atualizacao}
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                  ))}
                </div>

                {renderPaginacao()}
              </>
            )}
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default LogAtualizacoes;
